import React, { useState, useEffect, useCallback, useId } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Plus, Trash2, Upload } from 'lucide-react';

let _idCounter = 0;
const genId = () => `uid-${++_idCounter}-${Date.now()}`;

export default function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [costItems, setCostItems] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [trucks, setTrucks] = useState([{ _uid: genId(), truck_no: '', driver_name: '' }]);
  const [isClosed, setIsClosed] = useState(false);
  
  const [formData, setFormData] = useState({
    job_type: '',
    special_remarks: '',
    job_card: '',
    customer_name: '',
    customer_contact_no: '',
    show_contact_in_pdf: true,
    property_type: '',
    apartment_size: '',
    total_job_amount: '',
    invoice_amount: 0,
    date: new Date().toISOString().split('T')[0],
    salesperson_id: '',
    location_from: '',
    location_from_apt: '',
    location_from_building: '',
    location_from_pin: '',
    location_to: '',
    location_to_apt: '',
    location_to_building: '',
    location_to_pin: '',
    job_done_by: 'Reef Team',
    third_party_name: '',
    third_party_amount: 0,
    payment_status_outsourced: '',
    moving_date: '',
    reporting_time: '',
    payment_status: '',
    payment_status_other: '',
    job_start_time: '09:00',
    job_end_time: '18:00',
    reef_truck_no: '',
    driver_name: '',
    supervisor_name: '',
    total_reef_staff: 0,
    number_outside_staff: 0,
    number_of_days: 1,
    painting_included: '',
    painting_amount: 0,
    curtain_fixing_included: '',
    curtain_fixing_amount: 0,
    storage_included: '',
    storage_amount: 0
  });

  const [teamMembers, setTeamMembers] = useState([]);

  const [days, setDays] = useState([
    { day_number: 1, line_items: [] },
    { day_number: 2, line_items: [] },
    { day_number: 3, line_items: [] }
  ]);

  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    fetchMasterData();
    if (isEdit) {
      fetchJob();
    } else {
      initializeLineItems();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMasterData = async () => {
    const fetchWithRetry = async (url, attempts = 3) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          const res = await axios.get(url);
          return res.data;
        } catch (err) {
          lastErr = err;
          // Short backoff before retrying — helps with flaky mobile networks
          await new Promise(r => setTimeout(r, 400 * (i + 1)));
        }
      }
      throw lastErr;
    };

    const [spResult, ciResult] = await Promise.allSettled([
      fetchWithRetry(`${API}/salespersons`),
      fetchWithRetry(`${API}/cost-items`),
    ]);

    const failed = [];
    if (spResult.status === 'fulfilled') {
      setSalespersons(spResult.value || []);
    } else {
      failed.push('salespersons');
      console.warn('JobForm: salespersons fetch failed', spResult.reason);
    }
    if (ciResult.status === 'fulfilled') {
      setCostItems(ciResult.value || []);
    } else {
      failed.push('cost items');
      console.warn('JobForm: cost-items fetch failed', ciResult.reason);
    }

    if (failed.length > 0) {
      toast.error(`Couldn't load ${failed.join(' and ')}. Tap Refresh below to retry.`, {
        action: {
          label: 'Retry',
          onClick: () => fetchMasterData(),
        },
        duration: 10000,
      });
    }
  };

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      const job = response.data;
      
      setFormData({
        job_card: job.job_card,
        job_type: job.job_type || 'Moving Job',
        special_remarks: job.special_remarks || '',
        customer_name: job.customer_name,
        customer_contact_no: job.customer_contact_no || '',
        show_contact_in_pdf: job.show_contact_in_pdf !== false,
        property_type: job.property_type || '',
        apartment_size: job.apartment_size || '',
        total_job_amount: job.total_job_amount || '',
        invoice_amount: job.invoice_amount,
        date: job.date,
        salesperson_id: job.salesperson_id,
        location_from: job.location_from || '',
        location_from_apt: job.location_from_apt || '',
        location_from_building: job.location_from_building || '',
        location_from_pin: job.location_from_pin || '',
        location_to: job.location_to || '',
        location_to_apt: job.location_to_apt || '',
        location_to_building: job.location_to_building || '',
        location_to_pin: job.location_to_pin || '',
        job_done_by: job.job_done_by || 'Reef Team',
        third_party_name: job.third_party_name || '',
        third_party_amount: job.third_party_amount || 0,
        payment_status_outsourced: job.payment_status_outsourced || '',
        moving_date: job.moving_date || job.date,
        reporting_time: job.reporting_time || '',
        payment_status: job.payment_status || '',
        payment_status_other: job.payment_status_other || '',
        job_start_time: job.job_start_time || '09:00',
        job_end_time: job.job_end_time || '18:00',
        reef_truck_no: job.reef_truck_no || '',
        driver_name: job.driver_name || '',
        supervisor_name: job.supervisor_name || '',
        total_reef_staff: job.total_reef_staff || 0,
        number_outside_staff: job.number_outside_staff || 0,
        number_of_days: job.number_of_days || 1,
        painting_included: job.painting_included || '',
        painting_amount: job.painting_amount || 0,
        curtain_fixing_included: job.curtain_fixing_included || '',
        curtain_fixing_amount: job.curtain_fixing_amount || 0,
        storage_included: job.storage_included || '',
        storage_amount: job.storage_amount || 0
      });
      
      if (job.team_members && job.team_members.length > 0) {
        setTeamMembers(job.team_members);
      }
      
      if (job.attachments && job.attachments.length > 0) {
        const parsed = job.attachments.map(a => {
          try { return JSON.parse(a); } catch { return a; }
        });
        setUploadedFiles(parsed);
      }
      
      if (job.trucks && job.trucks.length > 0) {
        setTrucks(job.trucks.map(t => ({ _uid: t._uid || genId(), ...t })));
      }

      setIsClosed(job.status === 'closed');

      // Ensure every line_item has a stable _uid for React keys
      const hydratedDays = (job.days || []).map(d => ({
        ...d,
        line_items: (d.line_items || []).map(li => ({ _uid: li._uid || genId(), ...li }))
      }));
      setDays(hydratedDays);
    } catch (error) {
      toast.error('Failed to load job');
    }
  };

  const initializeLineItems = () => {
    const newDays = [1, 2, 3].map(dayNum => ({
      day_number: dayNum,
      line_items: [
        ...costItems.map((item, idx) => ({
          _uid: genId(),
          item_name: item.name,
          cost: item.cost,
          quantity: 0,
          total: 0,
          is_fixed: true,
          order: idx,
          remarks: ''
        })),
        ...Array(4).fill(null).map((_, idx) => ({
          _uid: genId(),
          item_name: '',
          cost: 0,
          quantity: 0,
          total: 0,
          is_fixed: false,
          order: costItems.length + idx,
          remarks: ''
        })),
        ...Array(8).fill(null).map((_, idx) => ({
          _uid: genId(),
          item_name: '',
          cost: 0,
          quantity: 0,
          total: 0,
          is_fixed: false,
          order: costItems.length + 4 + idx,
          remarks: ''
        }))
      ]
    }));
    setDays(newDays);
  };

  useEffect(() => {
    if (costItems.length > 0 && !isEdit) {
      initializeLineItems();
    }
  }, [costItems]);

  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return { totalHours: 0, overtimeHours: 0, overtimeAmount: 0 };
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let totalHours = (endHour + endMin / 60) - (startHour + startMin / 60);
    if (totalHours < 0) totalHours += 24;
    
    const overtimeHours = Math.max(0, totalHours - 10);
    const overtimeAmount = overtimeHours * 5; // 5 AED per overtime hour
    
    return { totalHours, overtimeHours, overtimeAmount };
  };

  const updateTeamMember = (index, field, value) => {
    const newMembers = [...teamMembers];
    newMembers[index][field] = value;
    
    if (field === 'start_time' || field === 'end_time') {
      const hours = calculateHours(newMembers[index].start_time, newMembers[index].end_time);
      newMembers[index].total_hours = hours.totalHours;
      newMembers[index].overtime_hours = hours.overtimeHours;
      newMembers[index].overtime_amount = hours.overtimeAmount;
    }
    
    setTeamMembers(newMembers);
  };

  // Recalculate all team members' overtime when job start/end times change
  useEffect(() => {
    if (teamMembers.length === 0) return;
    const updated = teamMembers.map(member => {
      // Only update members whose times match the previous job times
      // or always sync to main times
      const hours = calculateHours(member.start_time, member.end_time);
      return {
        ...member,
        total_hours: hours.totalHours,
        overtime_hours: hours.overtimeHours,
        overtime_amount: hours.overtimeAmount
      };
    });
    setTeamMembers(updated);
  }, [formData.job_start_time, formData.job_end_time]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTeamMember = () => {
    const hours = calculateHours(formData.job_start_time, formData.job_end_time);
    setTeamMembers([...teamMembers, {
      _uid: genId(),
      name: '',
      start_time: formData.job_start_time,
      end_time: formData.job_end_time,
      total_hours: hours.totalHours,
      overtime_hours: hours.overtimeHours,
      overtime_amount: hours.overtimeAmount
    }]);
  };

  const removeTeamMember = (index) => {
    const newMembers = teamMembers.filter((_, i) => i !== index);
    setTeamMembers(newMembers);
  };

  const updateLineItem = (dayIndex, itemIndex, field, value) => {
    const newDays = [...days];
    const item = newDays[dayIndex].line_items[itemIndex];
    item[field] = value;
    
    if (field === 'cost' || field === 'quantity') {
      item.total = (parseFloat(item.cost) || 0) * (parseFloat(item.quantity) || 0);
    }
    
    setDays(newDays);
  };

  const addLineItem = (dayIndex) => {
    const newDays = [...days];
    const currentItems = newDays[dayIndex].line_items;
    newDays[dayIndex].line_items.push({
      _uid: genId(),
      item_name: '',
      cost: 0,
      quantity: 0,
      total: 0,
      is_fixed: false,
      order: currentItems.length,
      remarks: ''
    });
    setDays(newDays);
  };

  const removeLineItem = (dayIndex, itemIndex) => {
    const newDays = [...days];
    newDays[dayIndex].line_items.splice(itemIndex, 1);
    setDays(newDays);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));
    
    try {
      toast.info(`Uploading ${files.length} file(s)...`);
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newFiles = response.data.map(f => ({
        original_name: f.original_name,
        stored_name: f.stored_name,
        url: f.url,
        content_type: f.content_type
      }));
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload files');
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const isImageFile = (file) => {
    if (typeof file === 'string') return /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
    return file.content_type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.original_name || '');
  };

  const getFileUrl = (file) => {
    if (typeof file === 'string') return file;
    return file.url;
  };

  const getFileName = (file) => {
    if (typeof file === 'string') return file;
    return file.original_name || file.stored_name;
  };

  const calculateTotals = () => {
    let totalCost = 0;
    days.forEach(day => {
      day.line_items.forEach(item => {
        totalCost += item.total;
      });
    });
    
    if (formData.job_done_by === 'Third Party' && formData.third_party_amount) {
      totalCost += parseFloat(formData.third_party_amount);
    }
    
    // Add additional service costs
    if (formData.painting_included === 'Yes' && formData.painting_amount) {
      totalCost += parseFloat(formData.painting_amount) || 0;
    }
    if (formData.curtain_fixing_included === 'Yes' && formData.curtain_fixing_amount) {
      totalCost += parseFloat(formData.curtain_fixing_amount) || 0;
    }
    if (formData.storage_included === 'Yes' && formData.storage_amount) {
      totalCost += parseFloat(formData.storage_amount) || 0;
    }
    
    const totalOvertimeAmount = teamMembers.reduce((sum, member) => sum + (member.overtime_amount || 0), 0);
    totalCost += totalOvertimeAmount;
    
    const margin = formData.invoice_amount - totalCost;
    return { totalCost, margin, totalOvertimeAmount };
  };

  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (!detail) return 'Failed to save job';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => {
        const field = d.loc ? d.loc[d.loc.length - 1] : 'unknown';
        return `${field}: ${d.msg}`;
      }).join(', ');
    }
    return 'Failed to save job';
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();

    // Validate required fields for non-draft submissions
    if (!isDraft) {
      const missing = [];
      if (!formData.job_type) missing.push('Job Type');
      if (!formData.job_card) missing.push('Job Card');
      if (!formData.customer_name) missing.push('Customer Name');
      if (!formData.date) missing.push('Date');
      if (!formData.salesperson_id) missing.push('Salesperson');
      if (!formData.location_from) missing.push('Location From');
      if (!formData.location_to) missing.push('Location To');

      if (formData.job_type === 'Shipping Job') {
        if (!formData.third_party_amount && formData.third_party_amount !== 0) missing.push('Amount Payable to Third Party');
      } else {
        // Full validation for Moving Job / Curtain / Painting
        if (!formData.invoice_amount && formData.invoice_amount !== 0) missing.push('Invoice Amount');
        if (formData.job_done_by !== 'Third Party' && !formData.payment_status) missing.push('Payment Status');
        if (!formData.reef_truck_no) missing.push('Reef Truck No');
        if (!formData.driver_name) missing.push('Driver Name');
        if (!formData.supervisor_name) missing.push('Supervisor Name');
        if (!formData.moving_date) missing.push('Moving Date');
        if (!formData.painting_included) missing.push('Painting Services Included');
        if (!formData.curtain_fixing_included) missing.push('New Curtain Service Included');
        if (!formData.storage_included) missing.push('Storage Service Included');
      }
      if (missing.length > 0) {
        toast.error(`Please fill required fields: ${missing.join(', ')}`);
        return;
      }
    } else {
      // Minimum validation for drafts
      if (!formData.job_type) {
        toast.error('Please select a Job Type to save as draft');
        return;
      }
      if (!formData.job_card) {
        toast.error('Please enter a Job Card number to save as draft');
        return;
      }
      if (!formData.customer_name) {
        toast.error('Please enter a Customer Name to save as draft');
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        total_job_amount: parseFloat(formData.total_job_amount) || 0,
        invoice_amount: parseFloat(formData.invoice_amount) || 0,
        third_party_amount: parseFloat(formData.third_party_amount) || 0,
        painting_amount: parseFloat(formData.painting_amount) || 0,
        curtain_fixing_amount: parseFloat(formData.curtain_fixing_amount) || 0,
        storage_amount: parseFloat(formData.storage_amount) || 0,
        total_reef_staff: parseInt(formData.total_reef_staff) || 0,
        number_outside_staff: parseInt(formData.number_outside_staff) || 0,
        number_of_days: parseInt(formData.number_of_days) || 1,
        team_members: teamMembers.filter(m => m.name).map(m => ({
          ...m,
          total_hours: parseFloat(m.total_hours) || 0,
          overtime_hours: parseFloat(m.overtime_hours) || 0,
          overtime_amount: parseFloat(m.overtime_amount) || 0
        })),
        attachments: uploadedFiles.map(f => typeof f === 'string' ? f : JSON.stringify(f)),
        trucks: trucks.filter(t => t.truck_no || t.driver_name),
        days: days.map(day => ({
          day_number: day.day_number,
          line_items: day.line_items
            .filter(item => item.item_name || item.quantity > 0)
            .map(item => ({
              ...item,
              cost: parseFloat(item.cost) || 0,
              quantity: parseFloat(item.quantity) || 0,
              total: parseFloat(item.total) || 0,
              remarks: item.remarks || ''
            }))
        })),
        status: isDraft ? 'draft' : 'submitted'
      };

      if (isEdit) {
        await axios.put(`${API}/jobs/${id}`, payload);
        toast.success(isDraft ? 'Draft saved successfully' : 'Job updated successfully');
      } else {
        await axios.post(`${API}/jobs`, payload);
        toast.success(isDraft ? 'Draft saved successfully' : 'Job created successfully');
      }
      
      navigate('/');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = (e) => {
    handleSubmit(e, true);
  };

  const { totalCost, margin, totalOvertimeAmount } = calculateTotals();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-form-page">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEdit ? 'Edit Job' : 'New Job'}
            </h1>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        {isClosed && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="closed-banner">
            <svg className="w-5 h-5 text-red-600 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2h.5A1.5 1.5 0 0117 10.5v6A1.5 1.5 0 0115.5 18h-11A1.5 1.5 0 013 16.5v-6A1.5 1.5 0 014.5 9H5zm2-2a3 3 0 116 0v2H7V7z" clipRule="evenodd"/></svg>
            <div className="text-sm text-red-800">
              <strong>This job is closed.</strong> Editing is locked. An admin can reopen it from the dashboard.
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Details Card - keeping it as is */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2 col-span-full">
                <Label htmlFor="job_type">Job Type <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.job_type}
                  onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                  required
                >
                  <SelectTrigger data-testid="job-type-select">
                    <SelectValue placeholder="Select Job Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Moving Job">Moving Job</SelectItem>
                    <SelectItem value="Shipping Job">Shipping Job</SelectItem>
                    <SelectItem value="Curtain">Curtain</SelectItem>
                    <SelectItem value="Painting">Painting</SelectItem>
                  </SelectContent>
                </Select>
                {!formData.job_type && (
                  <p className="text-sm text-slate-600">Please select a job type to proceed</p>
                )}
              </div>
              <div className="space-y-2 col-span-full">
                <Label htmlFor="special_remarks">Special Remarks</Label>
                <Textarea
                  id="special_remarks"
                  rows={3}
                  value={formData.special_remarks}
                  onChange={(e) => setFormData({ ...formData, special_remarks: e.target.value })}
                  placeholder="Any special instructions, fragile items, access notes, elevator info, etc."
                  data-testid="special-remarks-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_card">Job Card <span className="text-red-500">*</span></Label>
                <Input
                  id="job_card"
                  value={formData.job_card}
                  onChange={(e) => setFormData({ ...formData, job_card: e.target.value })}
                  required
                  data-testid="job-card-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name <span className="text-red-500">*</span></Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_contact_no">Customer Contact No</Label>
                <Input
                  id="customer_contact_no"
                  value={formData.customer_contact_no}
                  onChange={(e) => setFormData({ ...formData, customer_contact_no: e.target.value })}
                  placeholder="+971 50 123 4567"
                  data-testid="customer-contact-no-input"
                />
                <div className="flex items-center gap-2 pt-1">
                  <input
                    id="show_contact_in_pdf"
                    type="checkbox"
                    checked={!!formData.show_contact_in_pdf}
                    onChange={(e) => setFormData({ ...formData, show_contact_in_pdf: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="show-contact-in-pdf-toggle"
                  />
                  <label htmlFor="show_contact_in_pdf" className="text-xs text-slate-600 cursor-pointer">
                    Show contact in PDF / Print
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger id="property_type" data-testid="property-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Apartment">Apartment</SelectItem>
                    <SelectItem value="Villa">Villa</SelectItem>
                    <SelectItem value="Office">Office</SelectItem>
                    <SelectItem value="Warehouse">Warehouse</SelectItem>
                    <SelectItem value="Store">Store</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="apartment_size">Apartment Size</Label>
                <Select
                  value={formData.apartment_size}
                  onValueChange={(value) => setFormData({ ...formData, apartment_size: value })}
                >
                  <SelectTrigger id="apartment_size" data-testid="apartment-size-select">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Studio">Studio</SelectItem>
                    <SelectItem value="1 BHK">1 BHK</SelectItem>
                    <SelectItem value="2 BHK">2 BHK</SelectItem>
                    <SelectItem value="3 BHK">3 BHK</SelectItem>
                    <SelectItem value="4 BHK">4 BHK</SelectItem>
                    <SelectItem value="5+ BHK">5+ BHK</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_job_amount">Total Job Amount (AED) <span className="text-xs text-slate-500">(With VAT)</span></Label>
                <Input
                  id="total_job_amount"
                  type="number"
                  step="0.01"
                  value={formData.total_job_amount || ''}
                  onChange={(e) => {
                    const totalWithVat = parseFloat(e.target.value) || 0;
                    const withoutVat = (totalWithVat / 1.05).toFixed(2);
                    setFormData({ 
                      ...formData, 
                      total_job_amount: e.target.value,
                      invoice_amount: withoutVat 
                    });
                  }}
                  placeholder="Enter amount with VAT"
                  data-testid="total-job-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoice_amount">Total Invoice Amount (AED) * (Without VAT) <span className="text-xs text-green-600">(Auto-calculated: -5%)</span></Label>
                <Input
                  id="invoice_amount"
                  type="number"
                  step="0.01"
                  value={formData.invoice_amount}
                  onChange={(e) => setFormData({ ...formData, invoice_amount: e.target.value })}
                  required
                  className="bg-slate-50"
                  data-testid="invoice-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date <span className="text-red-500">*</span></Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salesperson">Salesperson <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.salesperson_id}
                  onValueChange={(value) => setFormData({ ...formData, salesperson_id: value })}
                  required
                >
                  <SelectTrigger data-testid="salesperson-select">
                    <SelectValue placeholder="Select salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    {salespersons.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 col-span-full">
                <Label>Location From <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    placeholder="Address"
                    value={formData.location_from}
                    onChange={(e) => setFormData({ ...formData, location_from: e.target.value })}
                    required
                    data-testid="location-from-input"
                  />
                  <Input
                    placeholder="Apartment Number"
                    value={formData.location_from_apt}
                    onChange={(e) => setFormData({ ...formData, location_from_apt: e.target.value })}
                    data-testid="location-from-apt-input"
                  />
                  <Input
                    placeholder="Building Name"
                    value={formData.location_from_building}
                    onChange={(e) => setFormData({ ...formData, location_from_building: e.target.value })}
                    data-testid="location-from-building-input"
                  />
                </div>
                <Input
                  placeholder="Location Pin (Google Maps URL)"
                  value={formData.location_from_pin}
                  onChange={(e) => setFormData({ ...formData, location_from_pin: e.target.value })}
                  data-testid="location-from-pin-input"
                />
              </div>
              <div className="space-y-2 col-span-full">
                <Label>Location To <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    placeholder="Address"
                    value={formData.location_to}
                    onChange={(e) => setFormData({ ...formData, location_to: e.target.value })}
                    required
                    data-testid="location-to-input"
                  />
                  <Input
                    placeholder="Apartment Number"
                    value={formData.location_to_apt}
                    onChange={(e) => setFormData({ ...formData, location_to_apt: e.target.value })}
                    data-testid="location-to-apt-input"
                  />
                  <Input
                    placeholder="Building Name"
                    value={formData.location_to_building}
                    onChange={(e) => setFormData({ ...formData, location_to_building: e.target.value })}
                    data-testid="location-to-building-input"
                  />
                </div>
                <Input
                  placeholder="Location Pin (Google Maps URL)"
                  value={formData.location_to_pin}
                  onChange={(e) => setFormData({ ...formData, location_to_pin: e.target.value })}
                  data-testid="location-to-pin-input"
                />
              </div>
              
              {/* Shipping Job: show only Third Party Amount */}
              {formData.job_type === 'Shipping Job' && (
                <div className="space-y-2 col-span-full">
                  <Label htmlFor="shipping_third_party_amount">Amount Payable to Third Party (AED) <span className="text-red-500">*</span></Label>
                  <Input
                    id="shipping_third_party_amount"
                    type="number"
                    step="0.01"
                    value={formData.third_party_amount}
                    onChange={(e) => setFormData({ ...formData, third_party_amount: e.target.value })}
                    required
                    placeholder="Enter amount"
                    className="max-w-sm"
                    data-testid="shipping-third-party-amount-input"
                  />
                </div>
              )}

              {/* Full details for non-Shipping jobs */}
              {formData.job_type !== 'Shipping Job' && (
              <>
              <div className="space-y-2">
                <Label htmlFor="job_done_by">Job Done By <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.job_done_by}
                  onValueChange={(value) => setFormData({ ...formData, job_done_by: value })}
                  required
                >
                  <SelectTrigger data-testid="job-done-by-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Reef Team">Reef Team</SelectItem>
                    <SelectItem value="Third Party">Third Party</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.job_done_by === 'Third Party' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="third_party_name">Third Party Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="third_party_name"
                      value={formData.third_party_name}
                      onChange={(e) => setFormData({ ...formData, third_party_name: e.target.value })}
                      required
                      data-testid="third-party-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="third_party_amount">Amount Payable to Third Party (AED) <span className="text-red-500">*</span></Label>
                    <Input
                      id="third_party_amount"
                      type="number"
                      step="0.01"
                      value={formData.third_party_amount}
                      onChange={(e) => setFormData({ ...formData, third_party_amount: e.target.value })}
                      required
                      data-testid="third-party-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Status for Outsourced Job <span className="text-red-500">*</span></Label>
                    <RadioGroup 
                      value={formData.payment_status_outsourced} 
                      onValueChange={(value) => setFormData({ ...formData, payment_status_outsourced: value })}
                      required
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Payment received to reef account through payment link" id="outsourced1" data-testid="outsourced-payment-1" />
                        <Label htmlFor="outsourced1" className="font-normal cursor-pointer">Payment received to reef account through payment link</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Received by outsourcing team" id="outsourced2" data-testid="outsourced-payment-2" />
                        <Label htmlFor="outsourced2" className="font-normal cursor-pointer">Received by outsourcing team</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Not Received" id="outsourced3" data-testid="outsourced-payment-3" />
                        <Label htmlFor="outsourced3" className="font-normal cursor-pointer">Not Received</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Other" id="outsourced4" data-testid="outsourced-payment-4" />
                        <Label htmlFor="outsourced4" className="font-normal cursor-pointer">Other</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="moving_date">Moving Date <span className="text-red-500">*</span></Label>
                <Input
                  id="moving_date"
                  type="date"
                  value={formData.moving_date}
                  onChange={(e) => setFormData({ ...formData, moving_date: e.target.value })}
                  required
                  data-testid="moving-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reporting_time">Reporting Time</Label>
                <Input
                  id="reporting_time"
                  type="time"
                  value={formData.reporting_time}
                  onChange={(e) => setFormData({ ...formData, reporting_time: e.target.value })}
                  data-testid="reporting-time-input"
                />
              </div>
              
              {formData.job_done_by !== 'Third Party' && (
              <div className="space-y-2 col-span-full">
                <Label>Payment Status <span className="text-red-500">*</span></Label>
                <RadioGroup 
                  value={formData.payment_status} 
                  onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                  required
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Received by payment link - Credit card" id="payment1" data-testid="payment-status-1" />
                    <Label htmlFor="payment1" className="font-normal cursor-pointer">Received by payment link - Credit card</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Received by cash" id="payment2" data-testid="payment-status-2" />
                    <Label htmlFor="payment2" className="font-normal cursor-pointer">Received by cash</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Received by Bank transfer" id="payment3" data-testid="payment-status-3" />
                    <Label htmlFor="payment3" className="font-normal cursor-pointer">Received by Bank transfer</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Not Received" id="payment4" data-testid="payment-status-4" />
                    <Label htmlFor="payment4" className="font-normal cursor-pointer">Not Received</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Other" id="payment5" data-testid="payment-status-5" />
                    <Label htmlFor="payment5" className="font-normal cursor-pointer">Other</Label>
                  </div>
                </RadioGroup>
                {!formData.payment_status && (
                  <p className="text-sm text-slate-600 mt-1">Please select a payment status</p>
                )}
                {formData.payment_status === 'Other' && (
                  <Input
                    placeholder="Please specify payment status"
                    value={formData.payment_status_other}
                    onChange={(e) => setFormData({ ...formData, payment_status_other: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="payment-status-other-input"
                  />
                )}
              </div>
              )}
              
              {/* Additional Services */}
              <div className="space-y-2 col-span-full">
                <Label>Painting Services Included? <span className="text-red-500">*</span></Label>
                <RadioGroup 
                  value={formData.painting_included} 
                  onValueChange={(value) => setFormData({ ...formData, painting_included: value, painting_amount: value === 'No' ? 0 : formData.painting_amount })}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="painting_yes" data-testid="painting-yes" />
                    <Label htmlFor="painting_yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="painting_no" data-testid="painting-no" />
                    <Label htmlFor="painting_no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {!formData.painting_included && (
                  <p className="text-sm text-slate-600 mt-1">Please select an option</p>
                )}
                {formData.painting_included === 'Yes' && (
                  <div className="mt-2">
                    <Label htmlFor="painting_amount">How Much to Pay to Reef Deco (AED)</Label>
                    <Input
                      id="painting_amount"
                      type="number"
                      step="0.01"
                      value={formData.painting_amount}
                      onChange={(e) => setFormData({ ...formData, painting_amount: e.target.value })}
                      placeholder="Enter amount"
                      className="mt-1 max-w-xs"
                      data-testid="painting-amount-input"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2 col-span-full">
                <Label>New Curtain Service Included? <span className="text-red-500">*</span></Label>
                <RadioGroup 
                  value={formData.curtain_fixing_included} 
                  onValueChange={(value) => setFormData({ ...formData, curtain_fixing_included: value, curtain_fixing_amount: value === 'No' ? 0 : formData.curtain_fixing_amount })}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="curtain_yes" data-testid="curtain-fixing-yes" />
                    <Label htmlFor="curtain_yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="curtain_no" data-testid="curtain-fixing-no" />
                    <Label htmlFor="curtain_no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {!formData.curtain_fixing_included && (
                  <p className="text-sm text-slate-600 mt-1">Please select an option</p>
                )}
                {formData.curtain_fixing_included === 'Yes' && (
                  <div className="mt-2">
                    <Label htmlFor="curtain_fixing_amount">How Much to Pay to Reef Deco (AED)</Label>
                    <Input
                      id="curtain_fixing_amount"
                      type="number"
                      step="0.01"
                      value={formData.curtain_fixing_amount}
                      onChange={(e) => setFormData({ ...formData, curtain_fixing_amount: e.target.value })}
                      placeholder="Enter amount"
                      className="mt-1 max-w-xs"
                      data-testid="curtain-fixing-amount-input"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2 col-span-full">
                <Label>Storage Service Included? <span className="text-red-500">*</span></Label>
                <RadioGroup 
                  value={formData.storage_included} 
                  onValueChange={(value) => setFormData({ ...formData, storage_included: value, storage_amount: value === 'No' ? 0 : formData.storage_amount })}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="storage_yes" data-testid="storage-yes" />
                    <Label htmlFor="storage_yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="storage_no" data-testid="storage-no" />
                    <Label htmlFor="storage_no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                {!formData.storage_included && (
                  <p className="text-sm text-slate-600 mt-1">Please select an option</p>
                )}
                {formData.storage_included === 'Yes' && (
                  <div className="mt-2">
                    <Label htmlFor="storage_amount">How Much to Pay to Reef Storage (AED)</Label>
                    <Input
                      id="storage_amount"
                      type="number"
                      step="0.01"
                      value={formData.storage_amount}
                      onChange={(e) => setFormData({ ...formData, storage_amount: e.target.value })}
                      placeholder="Enter amount"
                      className="mt-1 max-w-xs"
                      data-testid="storage-amount-input"
                    />
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="job_start_time">Job Start Time <span className="text-red-500">*</span></Label>
                <Input
                  id="job_start_time"
                  type="time"
                  value={formData.job_start_time}
                  onChange={(e) => setFormData({ ...formData, job_start_time: e.target.value })}
                  required
                  data-testid="job-start-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_end_time">Job End Time <span className="text-red-500">*</span></Label>
                <Input
                  id="job_end_time"
                  type="time"
                  value={formData.job_end_time}
                  onChange={(e) => setFormData({ ...formData, job_end_time: e.target.value })}
                  required
                  data-testid="job-end-time-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reef_truck_no">Reef Truck No <span className="text-red-500">*</span></Label>
                <Input
                  id="reef_truck_no"
                  value={formData.reef_truck_no}
                  onChange={(e) => setFormData({ ...formData, reef_truck_no: e.target.value })}
                  required
                  data-testid="reef-truck-no-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver_name">Driver Name <span className="text-red-500">*</span></Label>
                <Input
                  id="driver_name"
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                  required
                  data-testid="driver-name-input"
                />
              </div>
              
              {/* Additional Trucks & Drivers */}
              <div className="col-span-full space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Additional Trucks & Drivers</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTrucks([...trucks, { _uid: genId(), truck_no: '', driver_name: '' }])}
                    data-testid="add-truck-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Truck/Driver
                  </Button>
                </div>
                {trucks.map((truck, idx) => (
                  <div key={truck._uid || idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`truck-row-${idx}`}>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Truck No</Label>
                      <Input
                        value={truck.truck_no}
                        onChange={(e) => {
                          const updated = [...trucks];
                          updated[idx].truck_no = e.target.value;
                          setTrucks(updated);
                        }}
                        placeholder="Truck number"
                        data-testid={`truck-no-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Driver Name</Label>
                      <Input
                        value={truck.driver_name}
                        onChange={(e) => {
                          const updated = [...trucks];
                          updated[idx].driver_name = e.target.value;
                          setTrucks(updated);
                        }}
                        placeholder="Driver name"
                        data-testid={`truck-driver-${idx}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setTrucks(trucks.filter((_, i) => i !== idx))}
                      className="text-red-500 h-10"
                      data-testid={`remove-truck-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="supervisor_name">Supervisor Name <span className="text-red-500">*</span></Label>
                <Input
                  id="supervisor_name"
                  value={formData.supervisor_name}
                  onChange={(e) => setFormData({ ...formData, supervisor_name: e.target.value })}
                  required
                  data-testid="supervisor-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="total_reef_staff">Total Number of Reef Staff <span className="text-red-500">*</span></Label>
                <Input
                  id="total_reef_staff"
                  type="number"
                  value={formData.total_reef_staff}
                  onChange={(e) => setFormData({ ...formData, total_reef_staff: e.target.value })}
                  required
                  data-testid="total-reef-staff-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="number_outside_staff">Number of Outside Staff <span className="text-red-500">*</span></Label>
                <Input
                  id="number_outside_staff"
                  type="number"
                  value={formData.number_outside_staff}
                  onChange={(e) => setFormData({ ...formData, number_outside_staff: e.target.value })}
                  required
                  data-testid="number-outside-staff-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="number_of_days">Number of Days Taken <span className="text-red-500">*</span></Label>
                <Input
                  id="number_of_days"
                  type="number"
                  value={formData.number_of_days}
                  onChange={(e) => setFormData({ ...formData, number_of_days: e.target.value })}
                  required
                  data-testid="number-of-days-input"
                />
              </div>
              </>
              )}
            </CardContent>
          </Card>

          {/* Sections only for non-Shipping jobs */}
          {formData.job_type !== 'Shipping Job' && (
          <>
          {/* Team Details Card */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Team Details</CardTitle>
            </CardHeader>
            <CardContent>
              {teamMembers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-600 mb-4">No team members added yet</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTeamMember}
                    data-testid="add-first-team-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team Member
                  </Button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Name</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Start Time</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">End Time</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Total Hours</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Overtime Hours</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Overtime Amount (AED)</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map((member, index) => (
                          <tr key={member._uid || index} className="border-b border-slate-100">
                            <td className="py-2 px-3">
                              <Input
                                value={member.name}
                                onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                                placeholder="Enter name"
                                className="border-slate-200"
                                data-testid={`team-member-${index}-name`}
                              />
                            </td>
                            <td className="py-2 px-3">
                              <Input
                                type="time"
                                value={member.start_time}
                                onChange={(e) => updateTeamMember(index, 'start_time', e.target.value)}
                                className="border-slate-200"
                                data-testid={`team-member-${index}-start-time`}
                              />
                            </td>
                            <td className="py-2 px-3">
                              <Input
                                type="time"
                                value={member.end_time}
                                onChange={(e) => updateTeamMember(index, 'end_time', e.target.value)}
                                className="border-slate-200"
                                data-testid={`team-member-${index}-end-time`}
                              />
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-medium text-slate-700">{member.total_hours.toFixed(2)}</div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-medium text-amber-600">{member.overtime_hours.toFixed(2)}</div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-semibold text-emerald-600">AED {member.overtime_amount.toFixed(2)}</div>
                            </td>
                            <td className="py-2 px-3">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTeamMember(index)}
                                data-testid={`team-member-${index}-delete`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={addTeamMember}
                    data-testid="add-team-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team Member
                  </Button>
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-slate-700">Total Overtime Amount:</span>
                      <span className="text-xl font-bold text-emerald-600">AED {totalOvertimeAmount.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Overtime calculated for hours exceeding 10 hours per day at 5 AED/hour</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Cost Breakdown Card */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={String(activeDay)} onValueChange={(v) => setActiveDay(parseInt(v))}>
                <TabsList className="bg-slate-100 p-1 rounded-lg mb-6">
                  <TabsTrigger value="0" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="day-1-tab">Day 1</TabsTrigger>
                  <TabsTrigger value="1" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="day-2-tab">Day 2</TabsTrigger>
                  <TabsTrigger value="2" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="day-3-tab">Day 3</TabsTrigger>
                </TabsList>

                {days.map((day, dayIndex) => (
                  <TabsContent key={day.day_number || dayIndex} value={String(dayIndex)}>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700 min-w-[180px] md:min-w-[250px]">Item Name</th>
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700 min-w-[80px]">Cost (AED)</th>
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700 min-w-[70px]">Quantity</th>
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700 min-w-[90px]">Total (AED)</th>
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700 min-w-[150px] md:min-w-[200px]">Remarks</th>
                            <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {day.line_items.map((item, itemIndex) => (
                            <tr key={item._uid || itemIndex} className="border-b border-slate-100">
                              <td className="py-2 px-3 min-w-[180px] md:min-w-[250px]">
                                <Input
                                  value={item.item_name}
                                  onChange={(e) => updateLineItem(dayIndex, itemIndex, 'item_name', e.target.value)}
                                  disabled={item.is_fixed}
                                  className="border-slate-200 w-full"
                                  data-testid={`day-${dayIndex + 1}-row-${itemIndex}-name`}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.cost}
                                  onChange={(e) => updateLineItem(dayIndex, itemIndex, 'cost', e.target.value)}
                                  className="border-slate-200"
                                  data-testid={`day-${dayIndex + 1}-row-${itemIndex}-cost`}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.quantity}
                                  onChange={(e) => updateLineItem(dayIndex, itemIndex, 'quantity', e.target.value)}
                                  className="border-slate-200"
                                  data-testid={`day-${dayIndex + 1}-row-${itemIndex}-quantity`}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <div className="font-medium text-slate-700" data-testid={`day-${dayIndex + 1}-row-${itemIndex}-total`}>
                                  AED {item.total.toFixed(2)}
                                </div>
                              </td>
                              <td className="py-2 px-3">
                                <Input
                                  value={item.remarks || ''}
                                  onChange={(e) => updateLineItem(dayIndex, itemIndex, 'remarks', e.target.value)}
                                  placeholder="Enter remarks"
                                  className="border-slate-200"
                                  data-testid={`day-${dayIndex + 1}-row-${itemIndex}-remarks`}
                                />
                              </td>
                              <td className="py-2 px-3">
                                {!item.is_fixed && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeLineItem(dayIndex, itemIndex)}
                                    data-testid={`day-${dayIndex + 1}-row-${itemIndex}-delete`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => addLineItem(dayIndex)}
                      data-testid={`day-${dayIndex + 1}-add-line-btn`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Line
                    </Button>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* File Upload Card */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Attachments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Label 
                    htmlFor="file-upload" 
                    className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg cursor-pointer hover:bg-slate-800"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Files
                  </Label>
                  <Input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Label 
                    htmlFor="camera-upload" 
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700"
                  >
                    <Upload className="w-4 h-4" />
                    Take Photo
                  </Label>
                  <Input
                    id="camera-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="camera-upload-input"
                  />
                  <span className="text-sm text-slate-600">Upload multiple pictures or documents</span>
                </div>
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-slate-700">Uploaded Files ({uploadedFiles.length}):</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={typeof file === 'object' ? (file.stored_name || index) : index} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200 gap-2">
                          {isImageFile(file) && (
                            <img
                              src={`${API.replace('/api', '')}${getFileUrl(file)}`}
                              alt={getFileName(file)}
                              className="w-10 h-10 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <span className="text-sm text-slate-700 truncate flex-1">{getFileName(file)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Totals Card */}
          <Card className="shadow-sm border-slate-200">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                  <span className="text-lg font-semibold text-slate-700">Total Cost:</span>
                  <span className="text-2xl font-bold text-slate-900" data-testid="total-cost-display">
                    AED {totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                  <span className="text-lg font-semibold text-slate-700">Margin:</span>
                  <span 
                    className={`text-2xl font-bold ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                    data-testid="margin-display"
                  >
                    AED {margin.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          </>
          )}

          <div className="flex gap-4 justify-start">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/')}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50" 
              disabled={loading || isClosed}
              onClick={handleSaveAsDraft}
              data-testid="save-draft-btn"
            >
              {loading ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button 
              type="submit" 
              className="bg-slate-900 hover:bg-slate-800" 
              disabled={loading || isClosed}
              data-testid="submit-job-btn"
            >
              {(() => {
                if (loading) return 'Saving...';
                return isEdit ? 'Update Job' : 'Submit Job';
              })()}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
